/*
  Case studies v2: <cs-trail> (sections/cs-statement.liquid).
  The stage is empty at rest. The four Signal symbols come from the section's <template data-shapes>.
  - Mouse/trackpad: a pool of POOL nodes cycles through the symbols in order. A
    node is spawned only after the pointer travels SPACING px; each spawn is one Web
    Animations call (transform/opacity only). Each shape has its own drift, spin and
    scale, so the trail feels varied rather than repeated.
  - Touch / no hover: shapes surface one at a time in free spots around the heading
    (never over it), float briefly and fade. At most AMBIENT_MAX are visible; nothing
    runs while the stage is off screen or the tab is hidden.
  - Reduced motion: nothing is spawned.
*/
(() => {
  if (customElements.get('cs-trail')) return;

  const POOL = 12;
  const SPACING = 110;
  const AMBIENT_MAX = 3;

  const fineQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Movement character per shape (same order as the template): how far it keeps
  // travelling, how much it turns, its size, how long it lives and its easing.
  const CHARACTER = [
    { drift: 30, spin: 8, scale: 1, life: 1600, ease: 'cubic-bezier(0.34, 1.3, 0.64, 1)' }, // core: steady, settles
    { drift: 34, spin: 40, scale: 0.95, life: 1700, ease: 'cubic-bezier(0.22, 1, 0.36, 1)' }, // field: slow orbit
    { drift: 56, spin: 6, scale: 0.95, life: 1300, ease: 'cubic-bezier(0.16, 1, 0.3, 1)' }, // flow: travels on
    { drift: 32, spin: -90, scale: 0.95, life: 1650, ease: 'cubic-bezier(0.65, 0, 0.35, 1)' } // loop: turns back
  ];

  const rand = (min, max) => min + Math.random() * (max - min);

  class CsTrail extends HTMLElement {
    connectedCallback() {
      const tpl = this.querySelector('template[data-shapes]');
      this.layer = this.querySelector('[data-layer]');
      this.copy = this.querySelector('[data-copy]');
      if (!tpl || !this.layer) return;
      this.shapes = [...tpl.content.children];
      if (!this.shapes.length) return;

      this.inView = false;
      this.ac = new AbortController();
      const opts = { signal: this.ac.signal };
      fineQuery.addEventListener('change', () => this.setup(), opts);
      reduceMotion.addEventListener('change', () => this.setup(), opts);
      document.addEventListener('visibilitychange', () => this.syncAmbient(), opts);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([e]) => {
          this.inView = e.isIntersecting;
          this.syncAmbient();
        }, { threshold: 0.2 });
        this.io.observe(this);
      }
      this.setup();
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      if (this.io) this.io.disconnect();
      this.teardown();
    }

    makeNodes(count) {
      this.nodes = Array.from({ length: count }, (_, i) => {
        const node = document.createElement('div');
        node.className = 'cs-st__node';
        node.dataset.shape = String(i % this.shapes.length);
        node.appendChild(this.shapes[i % this.shapes.length].cloneNode(true));
        return node;
      });
      this.layer.replaceChildren(...this.nodes);
      this.cursor = 0;
      this.z = 1;
    }

    setup() {
      this.teardown();
      if (reduceMotion.matches || !this.layer.animate) return;
      this.modeAc = new AbortController();
      if (fineQuery.matches) {
        this.mode = 'trail';
        this.makeNodes(POOL);
        this.last = null;
        const opts = { signal: this.modeAc.signal, passive: true };
        this.addEventListener('pointermove', (e) => this.onMove(e), opts);
        this.addEventListener('pointerleave', () => (this.last = null), opts);
      } else {
        this.mode = 'ambient';
        this.makeNodes(this.shapes.length);
        this.syncAmbient();
      }
    }

    teardown() {
      if (this.modeAc) this.modeAc.abort();
      this.modeAc = null;
      this.mode = null;
      clearTimeout(this.ambientTimer);
      this.ambientTimer = null;
      (this.nodes || []).forEach((n) => n.getAnimations().forEach((a) => a.cancel()));
      this.nodes = null;
      this.spots = [];
      if (this.layer) this.layer.replaceChildren();
    }

    // ---------- Cursor trail ----------
    onMove(e) {
      if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
      if (!this.last) {
        this.last = { x: e.clientX, y: e.clientY };
        return;
      }
      const dx = e.clientX - this.last.x;
      const dy = e.clientY - this.last.y;
      const dist = Math.hypot(dx, dy);
      if (dist < SPACING) return;
      this.last = { x: e.clientX, y: e.clientY };
      const rect = this.getBoundingClientRect();
      this.spawnTrail(e.clientX - rect.left, e.clientY - rect.top, dx / dist, dy / dist);
    }

    spawnTrail(x, y, ux, uy) {
      const node = this.nodes[this.cursor];
      this.cursor = (this.cursor + 1) % this.nodes.length;
      const c = CHARACTER[+node.dataset.shape % CHARACTER.length];
      const dir = Math.random() < 0.5 ? -1 : 1;
      const r0 = rand(-12, 12);
      const r1 = r0 + dir * c.spin * 0.5;
      const r2 = r0 + dir * c.spin;
      const s = c.scale * rand(0.9, 1.06);
      const at = (k, lift = 0) => `translate(${x + ux * c.drift * k}px, ${y + uy * c.drift * k + lift}px)`;

      node.getAnimations().forEach((a) => a.cancel());
      node.style.zIndex = String(++this.z);
      node.animate(
        [
          { transform: `${at(-0.5)} scale(0.2) rotate(${r0}deg)`, opacity: 0 },
          { transform: `${at(0)} scale(${s * 1.05}) rotate(${r1}deg)`, opacity: 1, offset: 0.22, easing: c.ease },
          { transform: `${at(0.75)} scale(${s}) rotate(${(r1 + r2) / 2}deg)`, opacity: 1, offset: 0.68, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' },
          { transform: `${at(1, 18)} scale(${s * 0.6}) rotate(${r2}deg)`, opacity: 0 }
        ],
        { duration: c.life, easing: 'linear', fill: 'forwards' }
      );
    }

    // ---------- Ambient (touch) ----------
    syncAmbient() {
      if (this.mode !== 'ambient') return;
      const run = this.inView && !document.hidden;
      if (run && !this.ambientTimer) this.queueAmbient(300);
      else if (!run && this.ambientTimer) {
        clearTimeout(this.ambientTimer);
        this.ambientTimer = null;
      }
    }

    queueAmbient(delay) {
      this.ambientTimer = setTimeout(() => {
        this.ambientTimer = null;
        this.spawnAmbient();
        if (this.mode === 'ambient' && this.inView && !document.hidden) this.queueAmbient(rand(1100, 2000));
      }, delay);
    }

    freeSpot() {
      const stage = this.getBoundingClientRect();
      const copy = this.copy.querySelector('h2') || this.copy;
      const c = copy.getBoundingClientRect();
      const keepOut = {
        // padded by roughly a shape's radius so no shape edge reaches the heading
        l: c.left - stage.left - 64,
        r: c.right - stage.left + 64,
        t: c.top - stage.top - 96,
        b: c.bottom - stage.top + 64
      };
      const m = 48;
      for (let tries = 0; tries < 24; tries++) {
        const x = rand(m, stage.width - m);
        const y = rand(m, stage.height - m);
        const inside = x > keepOut.l && x < keepOut.r && y > keepOut.t && y < keepOut.b;
        const crowded = (this.spots || []).some((p) => Math.hypot(p.x - x, p.y - y) < 120);
        if (!inside && !crowded) return { x, y };
      }
      return null;
    }

    spawnAmbient() {
      const busy = this.nodes.filter((n) => n.getAnimations().some((a) => a.playState === 'running'));
      if (busy.length >= AMBIENT_MAX) return;
      const free = this.nodes.filter((n) => !busy.includes(n));
      const node = free[this.cursor++ % free.length];
      const spot = this.freeSpot();
      if (!node || !spot) return;
      this.spots = [...(this.spots || []), spot];

      const r0 = rand(-20, 20);
      const life = rand(3800, 5200);
      const rise = rand(14, 30);
      const at = (k) => `translate(${spot.x}px, ${spot.y - rise * k}px)`;
      const anim = node.animate(
        [
          { transform: `${at(0)} scale(0.6) rotate(${r0}deg)`, opacity: 0 },
          { transform: `${at(0.3)} scale(1) rotate(${r0 + 6}deg)`, opacity: 0.95, offset: 0.25 },
          { transform: `${at(0.75)} scale(1) rotate(${r0 + 10}deg)`, opacity: 0.95, offset: 0.72 },
          { transform: `${at(1)} scale(0.85) rotate(${r0 + 14}deg)`, opacity: 0 }
        ],
        { duration: life, easing: 'cubic-bezier(0.45, 0, 0.55, 1)', fill: 'forwards' }
      );
      anim.finished
        .then(() => (this.spots = (this.spots || []).filter((p) => p !== spot)))
        .catch(() => {});
    }
  }

  customElements.define('cs-trail', CsTrail);
})();
